/**
 * When did the current recording start?
 *
 * THE SINGLE OWNER of that instant. Before this module the answer was held
 * twice — as a `startTime` field on the module-private `Timer` singleton in
 * `src/models/timerModel.ts`, and as `state.timerModel.startTime` in the
 * Rematch slice the same class dispatches into — and the two DIVERGE. `stop()`
 * and `stopWithDuration()` clear the field and dispatch nothing, so after a
 * stop the slice still reports the instant of the recording that just ended;
 * `pause()` clears neither. The field is now gone and every use inside the
 * `Timer` class reads or writes the value below, so the tick loop and the stop
 * paths can no longer disagree about it.
 *
 * The slice copy is deliberately left as it is. Two production readers still
 * take it — the five-hour auto-stop guard and the backup-recording upload,
 * which turns it into an `endTime` on an upload payload — and the second one
 * consumes the stale-after-stop value. Changing what those readers see is a
 * behaviour change, not a containment step, so this slice moves no state out
 * of the store and alters no dispatched value.
 *
 * Three properties are load-bearing, and the co-located test asserts each:
 *
 *   - It is a LEAF: it imports NOTHING. `timerModel.ts` sits on the static
 *     cycle `store.ts -> models/index.ts -> timerModel.ts -> store.ts`, and a
 *     module with no imports at all cannot join it — which is what lets a
 *     later slice move this register behind a package door without dragging
 *     that cycle along.
 *   - It is RUNTIME-ONLY and NEVER PERSISTED. `timerModel` is absent from
 *     `persistConfig.whitelist` in `src/store.ts`, so the instant it mirrors
 *     has never survived a reload; a persisted copy would resurrect the start
 *     instant of a recording that ended in a previous session and hand it to
 *     the upload payload.
 *   - It is NEVER SEEDED. There is no initialiser and no hydrate entry point:
 *     the only value that can be read back is one `Timer.start()` wrote during
 *     this runtime.
 *
 * Every read is SYNCHRONOUS and must stay so. The register has no reactive
 * reader: the tick loop reads it inside a `setInterval` body and nothing
 * re-renders when it changes. Do not turn it into a jotai atom or wrap it in a
 * hook — a reactive subscription cannot be swapped back for a snapshot getter
 * later without changing who re-renders.
 */

let recordingStartedAt: number | null = null;

/** The instant the current recording started, or `null` when none is running. */
export function readRecordingStartedAt(): number | null {
  return recordingStartedAt;
}

/** Called by `Timer.start()` with the instant it computed. */
export function setRecordingStartedAt(ms: number): void {
  recordingStartedAt = ms;
}

/** Called by `Timer.stop()` and `Timer.stopWithDuration()`. Never by `pause()`. */
export function clearRecordingStartedAt(): void {
  recordingStartedAt = null;
}
